import { useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
} from "@mui/material";
import { AddCircle, TrashBinTrash, Pen } from "@solar-icons/react/ssr";
import { request } from "@/Api";
import { store, storeBid } from "@/sheet/bidStore";
import { useDispatch } from "react-redux";
import { setUpdating } from "@/sheet/bidSlice";
import { uuidv4 } from "@/utils";
import { updateDynamicColumnsFromBid } from "./bidColumns";

export default function SubQuantityColumnsModal({
    isOpen,
    onClose,
    bidId,
    refreshBid,
}) {
    const dispatch = useDispatch();
    const [columns, setColumns] = useState(
        store.bid?.sub_quantity_columns || [],
    );
    const [editingColId, setEditingColId] = useState(null);
    const [editingColName, setEditingColName] = useState("");
    const [newColName, setNewColName] = useState("");

    // Synchronize columns with store when modal opens
    // We can also just rely on store.bid.sub_quantity_columns.

    const handleSave = () => {
        dispatch(setUpdating(true));

        const oldColIds = (store.bid?.sub_quantity_columns || []).map(
            (c) => c.id,
        );
        const newCols = columns.filter((c) => !oldColIds.includes(c.id));
        const newColIdsToVisible = newCols.map((c) => `sub_quantities.${c.id}`);

        const updatedVisibleColumns = store.bid.visible_columns
            ? [...store.bid.visible_columns, ...newColIdsToVisible]
            : [...newColIdsToVisible];

        request
            .patch(`bids/${bidId}`, {
                sub_quantity_columns: columns,
                visible_columns: updatedVisibleColumns,
            })
            .then(() => request.get(`bids/${bidId}`))
            .then((res) => {
                const newBid = res.data;
                storeBid(newBid);
                updateDynamicColumnsFromBid(newBid);
                refreshBid(newBid);
                onClose();
            })
            .finally(() => {
                dispatch(setUpdating(false));
            });
    };

    const handleAdd = () => {
        if (!newColName.trim()) return;
        setColumns([...columns, { id: uuidv4(), name: newColName.trim() }]);
        setNewColName("");
    };

    const handleDelete = (id) => {
        setColumns(columns.filter((col) => col.id !== id));
    };

    const handleEditStart = (col) => {
        setEditingColId(col.id);
        setEditingColName(col.name);
    };

    const handleEditSave = (id) => {
        setColumns(
            columns.map((col) =>
                col.id === id ? { ...col, name: editingColName.trim() } : col,
            ),
        );
        setEditingColId(null);
        setEditingColName("");
    };

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Sub-Quantity Columns</DialogTitle>
            <DialogContent>
                <div
                    style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "10px",
                        marginBottom: "20px",
                    }}
                >
                    <TextField
                        size="small"
                        label="New Column Name"
                        value={newColName}
                        onChange={(e) => setNewColName(e.target.value)}
                        fullWidth
                    />
                    <Button
                        variant="contained"
                        onClick={handleAdd}
                        startIcon={<AddCircle />}
                    >
                        Add
                    </Button>
                </div>
                <List>
                    {columns.map((col) => (
                        <ListItem key={col.id}>
                            {editingColId === col.id ? (
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "10px",
                                        width: "100%",
                                    }}
                                >
                                    <TextField
                                        size="small"
                                        value={editingColName}
                                        onChange={(e) =>
                                            setEditingColName(e.target.value)
                                        }
                                        fullWidth
                                    />
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => handleEditSave(col.id)}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => setEditingColId(null)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <ListItemText primary={col.name} />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            onClick={() => handleEditStart(col)}
                                        >
                                            <Pen size={18} />
                                        </IconButton>
                                        <IconButton
                                            edge="end"
                                            onClick={() => handleDelete(col.id)}
                                        >
                                            <TrashBinTrash size={18} />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </>
                            )}
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave}>
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
}
